import type {FormDraftRecord} from "@/src/modules/core/lib/form-draft";
import {residentContextService} from "@/src/modules/core/services/resident-context";
import {userPropertyService} from "@/src/modules/core/services/user-property";
import {useComplianceBusyStore} from "@/src/modules/core/store/compliance-busy";
import {useFormDraftResumeStore} from "@/src/modules/core/store/form-draft-resume";
import type {Me} from "@/src/modules/core/types";
import type {Property} from "@/src/modules/properties/types";

function resolveContextProperty(
  draft: FormDraftRecord,
  propertyById: Map<string, Property>,
): Property | null {
  if (draft.contextPropertyId != null) {
    return propertyById.get(String(draft.contextPropertyId)) ?? null;
  }

  const formProperty = draft.values.property;
  if (formProperty && typeof formProperty === "object" && "id" in formProperty) {
    return propertyById.get(String((formProperty as {id: number}).id)) ?? null;
  }

  return null;
}

function resolveDraftHouseholdId(draft: FormDraftRecord): number | null {
  const household = draft.values.household;
  if (household && typeof household === "object" && "house_hold_id" in household) {
    const id = (household as {house_hold_id?: number}).house_hold_id;
    if (id != null && id !== 0) return id;
  }
  return null;
}

/** Restore global property context and pending draft for cold-start / post-login resume. */
export function hydrateResumableFormDraft(
  draft: FormDraftRecord,
  meProperties: Property[],
  me?: Me | null,
): void {
  const propertyById = new Map(meProperties.map((property) => [String(property.id), property]));
  const contextProperty = resolveContextProperty(draft, propertyById);
  if (contextProperty) {
    if (me?.isResident) {
      residentContextService.syncPropertyContext(
        me,
        contextProperty,
        resolveDraftHouseholdId(draft),
        {clearFilters: false},
      );
    } else {
      userPropertyService.setUserProperty(contextProperty);
    }
  }
  useFormDraftResumeStore.getState().setPending(draft);
  useComplianceBusyStore.getState().syncFormDraftDeferral();
}
